import React, { useEffect, useRef, useState } from "react";
import { Modal } from "@mantine/core";
import ConfigureMFA from "./ConfigureMFA";
import { connect } from "react-redux";

import API from "../utilities/LocalApiProxy";
import buildData from "../buildData/buildData";
import { showBuildMismatch } from "../actions/globalSnackbarActions";
import { updateUserRights } from "../actions/userAuthActions";

function sendTelemetryData({
	sessionStartTime,
	setDialogOpen,
	setDialogType,
	setStopTelemetry,
	updateUserRights,
	setBuildMismatchCount,
	showBuildMismatch,
}) {
	try {
		// We could include more user-specific info here if needed
		const sessionDurationMs = Date.now() - sessionStartTime;

		// Convert to human readable format
		const days = Math.floor(sessionDurationMs / (1000 * 60 * 60 * 24));
		const hours = Math.floor(
			(sessionDurationMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
		);
		const minutes = Math.floor(
			(sessionDurationMs % (1000 * 60 * 60)) / (1000 * 60)
		);
		const seconds = Math.floor((sessionDurationMs % (1000 * 60)) / 1000);
		const sessionDurationHuman = `${days}d ${hours}h ${minutes}m ${seconds}s`;

		const telemetryData = {
			currentPath: window.location.pathname,
			userAgent: navigator.userAgent,
			platform: navigator.platform,
			sessionStartTime,
			sessionDurationMs,
			sessionDurationHuman,
			frontendBuildNumber: buildData.buildNumber,
			branch: buildData.branch,
			runtimeEnvironment: buildData.runtimeEnvironment,
			timestamp: new Date().toISOString(),
			userBrowserWidth: window.innerWidth,
			userBrowserHeight: window.innerHeight,
			userScreenWidth: window.screen.width,
			userScreenHeight: window.screen.height,
		};

		// Fire and forget - don't block on response
		API.post(
			"/telemetry",
			{ body: telemetryData },
			{ suppressRateLimitSnackbar: true }
		)
			.then((res) => {
				const responseData = res?.mysql || {};
				const doesUserNeedToSetupMFA =
					responseData.doesUserNeedToSetupMFA || false;
				const buildMismatch = responseData.buildMismatch || false;

				if (buildMismatch) {
					console.error(
						"build number mismatch detected between frontend and backend!"
					);
					setBuildMismatchCount((prevCount) => {
						const newCount = prevCount + 1;
						// Show snackbar after 2 consecutive mismatches
						if (newCount >= 2) {
							showBuildMismatch();
						}
						return newCount;
					});
				}

				setStopTelemetry(responseData.stopTelemetry || false);

				if (responseData.userRights) {
					updateUserRights(responseData.userRights);
				}

				if (doesUserNeedToSetupMFA) {
					setDialogType("mfa_setup");
					setDialogOpen(true);
				}
			})
			.catch((err) => {
				// Silent failure - telemetry should never disrupt user experience
				console.debug("Telemetry send failed:", err);
			});
	} catch (error) {
		// Catch any errors in data collection
		console.debug("Telemetry collection error:", error);
	}
}

const dialogTitles = {
	mfa_setup: (
		<div
			className="MainFontBold"
			style={{ textAlign: "center", fontSize: 17 }}
		>
			Multi-Factor Authentication Setup Required
		</div>
	),
};

const dialogContents = {
	mfa_setup: <ConfigureMFA mode="required" />,
};

const TELEMETRY_INTERVAL_MS = 20 * 60 * 1000; // 20 minutes
const INITIAL_DELAY_MS = 2 * 1000; // 2 seconds
const MFA_COMPLETION_SIGNAL_TTL_MS = 60 * 1000; // 1 minute

const TelemetryReporterInner = (props) => {
	const [sessionStartTime] = useState(Date.now());
	const [dialogOpen, setDialogOpen] = useState(false);
	const [dialogType, setDialogType] = useState("");
	const [stopTelemetry, setStopTelemetry] = useState(false);
	const [, setBuildMismatchCount] = useState(0);
	const intervalRef = useRef(null);

	// Clean up stale MFA completion signals on mount
	useEffect(() => {
		const storedValue = localStorage.getItem("mfa_setup_completed");
		if (storedValue) {
			const timestamp = parseInt(storedValue, 10);
			const age = Date.now() - timestamp;

			// Remove if older than TTL
			if (age >= MFA_COMPLETION_SIGNAL_TTL_MS) {
				localStorage.removeItem("mfa_setup_completed");
			}
		}
	}, []); // Run once on mount

	// Close dialog when MFA is successfully completed in this tab
	useEffect(() => {
		if (props.shouldCloseRequiredMfaModal && dialogOpen) {
			setDialogOpen(false);
		}
	}, [props.shouldCloseRequiredMfaModal, dialogOpen]);

	// Listen for MFA completion in other tabs (storage event)
	useEffect(() => {
		const handleStorageChange = (e) => {
			if (e.key === "mfa_setup_completed" && dialogOpen) {
				const timestamp = parseInt(e.newValue, 10);
				const age = Date.now() - timestamp;

				// Only respond to recent signals (within 1 minute)
				if (age < MFA_COMPLETION_SIGNAL_TTL_MS) {
					setDialogOpen(false);
				}
			}
		};

		window.addEventListener("storage", handleStorageChange);
		return () => window.removeEventListener("storage", handleStorageChange);
	}, [dialogOpen]);

	useEffect(() => {
		// If backend says to stop telemetry, clear the interval
		if (stopTelemetry) {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
			return;
		}

		// Only set up telemetry if we don't already have an interval running
		if (!intervalRef.current) {
			// Send initial telemetry after a short delay to allow app to stabilize
			const initialTimeout = setTimeout(() => {
				sendTelemetryData({
					sessionStartTime,
					setDialogOpen,
					setDialogType,
					setStopTelemetry,
					updateUserRights: props.updateUserRights,
					setBuildMismatchCount,
					showBuildMismatch: props.showBuildMismatch,
				});
			}, INITIAL_DELAY_MS);

			// Set up interval for subsequent telemetry sends every 20 minutes
			intervalRef.current = setInterval(() => {
				sendTelemetryData({
					sessionStartTime,
					setDialogOpen,
					setDialogType,
					setStopTelemetry,
					updateUserRights: props.updateUserRights,
					setBuildMismatchCount,
					showBuildMismatch: props.showBuildMismatch,
				});
			}, TELEMETRY_INTERVAL_MS);

			// Cleanup function to clear timeout and interval on unmount
			return () => {
				clearTimeout(initialTimeout);
				if (intervalRef.current) {
					clearInterval(intervalRef.current);
				}
			};
		}
	}, [stopTelemetry]);

	return (
		<Modal
			opened={dialogOpen}
			onClose={() => {}}
			size="xs"
			withCloseButton={false}
			closeOnClickOutside={false}
			closeOnEscape={false}
			title={dialogTitles[dialogType]}
		>
			<div style={{ alignSelf: "center", paddingBottom: 16 }}>
				{dialogContents[dialogType]}
			</div>
		</Modal>
	);
};

const mapStateToProps = (state) => ({
	shouldCloseRequiredMfaModal: state.user.shouldCloseRequiredMfaModal,
});

export const TelemetryReporter = connect(mapStateToProps, {
	showBuildMismatch,
	updateUserRights,
})(TelemetryReporterInner);
